/**
 * @module AuroraServices
 * @description Singleton module that manages Aurora Services.
 */
const AuroraServices = (function () {
  /**
   * An object representing the module's settings.
   *
   * @private
   * @type {object} settings
   * @description The initial settings for the singleton object
   */
  const settings = {
    name: "aurora-services.js",
    debugging: true,
  };

  /**
   * Enable logging for the module.
   *
   * @type {boolean}
   */
  const LOGGING = Boolean(window?.theme?.log?.all || window?.theme?.log?.services);

  /**
   * Creates a request configuration object with default values for method and headers.
   *
   * @private
   * @param {object} optionalSettings - Optional settings to override the defaults.
   * @returns {object} The request configuration object.
   *
   * @author Brenden Mersey
   */
  const requestConfigFactory = (optionalSettings = {}) => {
    const defaults = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    };
    return { ...defaults, ...optionalSettings };
  };

  const withClaimlineTrackingId = (item = {}) => {
    const currentProperties =
      item && typeof item.properties === "object" && item.properties !== null ? item.properties : {};
    if (currentProperties._claimline_tracking_id) {
      return { ...item, properties: { ...currentProperties } };
    }
    const variantId = Number(item?.id || item?.variant_id);
    if (Number.isNaN(variantId) || variantId === 0) {
      return { ...item, properties: { ...currentProperties } };
    }
    return { ...item, properties: { ...currentProperties, _claimline_tracking_id: String(variantId) } };
  };

  const withClaimlineTrackingIds = (items = []) => {
    const list = Array.isArray(items) ? items : [];
    return list.map((item) => withClaimlineTrackingId(item));
  };

  /**
   * Adds products to the cart.
   *
   * @async
   * @param {object[]} items - The items to add to the cart.
   * @param {function} callback - The callback function to call when the request is complete.
   * @returns {Promise<object>} A promise that resolves to the response data.
   *
   * @description If an added item was already in the cart, then the quantity is equal to the new quantity for that
   * cart line item. However, if the same items have differing prices, properties, or selling plans, then
   * they'll be split into their own line items. The properties object must be an exact match to the existing line item
   * in the cart.
   *
   * @author Brenden Mersey
   */
  const addProductsToCart = async (items = [], callback = false) => {
    LOGGING && console.log("[ async addProductsToCart() ] :: Init :: items beging added to cart and callback...", { items, callback });

    // initialize status and data variables
    let data, response, responseBody, status, itemsToAdd;

    const trackedItems = withClaimlineTrackingIds(items);

    // get cart
    const cart = await getCart();

    // log cart items and new items being added to cart (before any updates)
    LOGGING &&
      console.log("[ async addProductsToCart() ] :: current cart items and new items being added...", {
        currentCartItems: cart.items,
        newItems: trackedItems,
      });

    // if cart has existing items
    if (cart.items.length) {
      // compare items being added to cart with items in cart by variant id
      // if there's a match, then we updated the properties of the item that will
      // be added to cart so that they don't creat a new cart line item

      const updatedItems = trackedItems.map((item) => {
        const currentCartItem = cart.items.find((cartItem) => cartItem.id === item.id);
        if (currentCartItem) {
          return {
            ...item,
            properties: {
              ...currentCartItem.properties,
              _claimline_tracking_id:
                currentCartItem.properties._claimline_tracking_id ||
                item.properties._claimline_tracking_id,
            },
          };
        }
        return item;
      });

      // log cart items and new items being added to cart (after updates)
      LOGGING &&
        console.log("[ async addProductsToCart() ] :: current cart items and updated items...", {
          currentCartItems: cart.items,
          updatedItems,
        });

      // create request body
      itemsToAdd = updatedItems;
      responseBody = JSON.stringify({ items: itemsToAdd });
    } else {
      // create request body
      itemsToAdd = trackedItems;
      responseBody = JSON.stringify({ items: itemsToAdd });
    }

    // create request config
    const requestConfig = requestConfigFactory({
      body: responseBody,
    });

    try {
      // get response from request to add items to cart
      response = await fetch(`${window.Shopify.routes.root}cart/add.js`, requestConfig);

      // convert response to json
      data = await response.json();

      // get response status
      status = response.status;

      // if status is 200, return data
      if (200 === status) {
        return data;
      } else {
        data = false;
        return data;
      }
    } catch (error) {
      // log error
      console.log("[ async addProductsToCart() ] :: Error", error);
    } finally {
      // dispatch custom 'aurora::addToCart' event
      document.dispatchEvent(
        new CustomEvent("aurora::addToCart", {
          detail: {
            data,
            items: itemsToAdd,
            response,
            status,
          },
        })
      );

      // if callback is provided, call it
      callback && callback();
    }
  };

  const changeCartByLineItemKey = async (key = "", quantity = 0, properties = {}, callback = false) => {
    if (settings.debugging) console.log("[ async changeCartByLineItemKey() ] :: Init");

    let response, status, data;

    const requestConfig = requestConfigFactory({
      body: JSON.stringify({
        id: key,
        quantity: quantity,
        properties: properties,
      }),
    });

    try {
      console.log(requestConfig, "CHECK requestConfig--------------------------------");
      response = await fetch(`${window.Shopify.routes.root}cart/change.js`, requestConfig);
      status = response.status;
      if (200 === status) {
        data = await response.json();
        // Check if items exist in the response data
        if (!data || !data.items) {
          console.error("No items found in the cart data");
          return;
        }

        const items = data.items;

        console.log(items); // Logs the array of items in the cart

        // Iterate over the items in the cart
        items.forEach((item) => {
          // Check if the item has an ID before proceeding
          if (!item.id) {
            console.log("Skipping item with no ID");
            return; // Skip the item if no ID
          }

          // Log item details
          console.log(item.id); // Logs item ID
          console.log(item.key); // Logs item key

          // Select the cart element using item ID as selector
          let cart_id = item.id;
          let cart_element = document.querySelector(`[data-quantity-variant-id="${cart_id}"]`);

          // If the cart element is found, set the cart-line-item-key attribute
          if (cart_element) {
            cart_element.setAttribute("data-cart-line-item-key", item.key);
          }
        });

        return data;
      } else {
        data = false;
        return data;
      }
    } catch (error) {
      console.log("[ async changeCartByLineItemKey() ] :: Error", error);
    } finally {
      const customEvent = new CustomEvent("aurora::cartLineItemChanged", {
        detail: {
          cart: data,
          status,
        },
      });
      document.dispatchEvent(customEvent);
      if (callback) {
        callback();
      }
    }
  };

  const clearCart = async (callback = false, isClearAttributes = false) => {
    if (settings.debugging) console.log("[ async clearCart() ] :: Init");

    let response, data;
    const requestConfig = requestConfigFactory();

    try {
      response = await fetch(window.Shopify.routes.root + "cart/clear.js", requestConfig);
      if (response.ok) {
        data = await response.json();

        if (isClearAttributes && data?.attributes) {
          clearCartAttributes(data);
        }

        return data;
      } else {
        data = false;
        return data;
      }
    } catch (error) {
      console.log("[ async clearCart() ] :: Error", error);
    } finally {
      if (callback) {
        callback();
      }
    }
  };

  /**
   * Fetches a theme section by ID via Shopify section rendering and returns its HTML.
   *
   * @async
   * @param {string} sectionId - The section ID to render (e.g. 'printable-orders').
   * @param {Object} options - The options used to configure the request (queryParams, path).
   * @param {function} callback - The callback function to call when the request is complete.
   * @returns {Promise<string|false>} A promise that resolves to the section HTML string, or false on invalid sectionId, non-ok response, or error.
   *
   * @description Pass a non-empty section ID; optional options control the request path and any extra query params so the section renders in the right context. The callback runs when the request finishes, whether it succeeded or failed.
   *
   * @author Brenden Mersey
   */
  const fetchSectionHtml = async (sectionId = "", options = {}, callback = false) => {
    LOGGING && console.log("[ async fetchSectionHtml() ] :: Init", { sectionId, options });

    let data = false;
    let response;

    const { queryParams = {}, path = window.location.pathname } = options;
    const params = new URLSearchParams(queryParams);
    params.set("section_id", sectionId);
    const requestConfig = requestConfigFactory({ method: "GET", headers: { Accept: "text/html" } });
    const requestUrl = `${path}?${params.toString()}`;

    try {
      response = await fetch(requestUrl, requestConfig);
      if (!response.ok) {
        console.warn("[ async fetchSectionHtml() ] :: Warning...", { status: response.status, statusText: response.statusText, sectionId });
        return data;
      }
      data = await response.text();
      return data;
    } catch (error) {
      console.error("[ async fetchSectionHtml() ] :: Error...", { error });
      return data;
    } finally {
      callback && callback();
    }
  };

  const getCart = async (callback = false) => {
    if (settings.debugging) console.log("[ async getCart() ] :: Init");

    let response, data;

    const requestConfig = requestConfigFactory({ method: "GET" });

    try {
      response = await fetch(`${window.Shopify.routes.root}cart.js`, requestConfig);
      if (response.ok) {
        data = await response.json();
        return data;
      } else {
        data = false;
        return data;
      }
    } catch (error) {
      console.log("[ async getCart() ] :: Error", error);
    } finally {
      if (callback) {
        callback();
      }
    }
  };

  const getCustomerByID = async (customerID = 0, accessToken = "", timestamp = "", signature = "", callback = false) => {
    if (settings.debugging) console.log("[ async getCustomerByID() ] :: Init");

    let response, data;

    const requestConfig = requestConfigFactory({
      method: "GET",
      data: {
        client_id: customerID,
        timestampb: timestamp,
        signatureb: signature,
      },
    });

    if (settings.debugging)
      console.log("[ async getCustomerByID() ] :: requestConfig and arguments", requestConfig, {
        customerID,
        accessToken,
        timestamp,
        signature,
        callback,
      });

    try {
      response = await fetch(`/apps/api/client/${customerID}?access_token=${accessToken}`, requestConfig);
      if (response.ok) {
        data = await response.json();
        return data;
      } else {
        data = false;
        return data;
      }
    } catch (error) {
      console.log("[ async getCustomerByID() ] :: Error", error);
    } finally {
      if (callback) {
        callback();
      }
    }
  };

  const getProductByHandle = async (productHandle = "", callback = false) => {
    if (settings.debugging) console.log("[ async getProductByHandle() ] :: Init");

    let response, data;
    const requestConfig = requestConfigFactory({ method: "GET" });

    try {
      response = await fetch(`${window.Shopify.routes.root}products/${productHandle}.js`, requestConfig);
      if (response.ok) {
        data = await response.json();
        return data;
      } else {
        data = false;
        return data;
      }
    } catch (error) {
      console.log("[ async getProductByHandle() ] :: Error", error);
    } finally {
      if (callback) {
        callback();
      }
    }
  };

  const getThemeAssetFromPath = async (path = "", callback = false) => {
    if (settings.debugging) console.log("[ async getThemeAssetFromPath() ] :: Init");

    let response, data;
    const requestConfig = requestConfigFactory({
      method: "GET",
    });

    try {
      response = await fetch(path, requestConfig);
      if (response.ok) {
        data = await response.json();
        return data;
      } else {
        data = false;
        return data;
      }
    } catch (error) {
      console.log("[ async getThemeAssetFromPath() ] :: Error", error);
    } finally {
      if (callback) {
        callback();
      }
    }
  };

  const updateCartAttribute = async (attributes = {}, callback = false) => {
    if (settings.debugging) console.log("[ async updateCartAttribute() ] :: Init");

    let response, data;
    const requestConfig = requestConfigFactory({
      body: JSON.stringify({ attributes }),
    });

    if (settings.debugging) console.log("[ async updateCartAttribute() ] :: attributes...", attributes);

    try {
      response = await fetch(window.Shopify.routes.root + "cart/update.js", requestConfig);
      if (response.ok) {
        data = await response.json();
        return data;
      } else {
        data = false;
        return data;
      }
    } catch (error) {
      console.log("[ async updateCartAttribute() ] :: Error", error);
    } finally {
      const customEvent = new CustomEvent("aurora::cartAttrsUpdated", {
        detail: { attributes, data },
      });
      document.dispatchEvent(customEvent);
      if (callback) {
        callback();
      }
    }
  };

  const clearCartAttributes = async (cart = null, callback = false) => {
    try {
      if (!cart) {
        cart = await getCart();
      }

      if (cart.attributes && Object.keys(cart.attributes).length > 0) {
        const emptyAttributes = {};

        for (const attribute in cart.attributes) {
          emptyAttributes[attribute] = null;
        }

        const updatedCart = await updateCartAttribute(emptyAttributes, callback);

        return updatedCart;
      }

      return false;
    } catch (error) {
      console.log("[ async clearCartAttributes() ] :: Error", error);
    }
  };

  const test = async (raasOptionAuth = "", accessToken = "", raasOptionApiKey = "", callback = false) => {
    let response, data;
    const url = `//${raasOptionAuth}/ssologin/setToken`;
    const params = new URLSearchParams({
      token: accessToken,
      apikey: raasOptionApiKey,
    }).toString();

    try {
      response = await fetch(`${url}?${params}`, {
        method: "GET",
        credentials: "include",
      });

      if (200 === response.status) {
        data = await response.json();
        console.log("[ async test() ] :: Success", data);
        return data;
      } else {
        data = false;
        return data;
      }
    } catch (error) {
      console.log("[ async test() ] :: Error", error);
    } finally {
      console.log("[ async test() ] :: Complete");
      if (callback) callback();
    }
  };

  const exchangeMultipassToken = async (lrtoken = "", lrshopifystore = "", raasOptionApiKey = "", handle = false, path = "/") => {
    let response, data;

    if (window.location.href.includes("account/login?order=")) {
      let orderId = window.location.href.slice(window.location.href.lastIndexOf("=") + 1);
      path = "/account/orders/" + orderId;
    }

    path = encodeURI(path);

    const url = `https://cloud-api.loginradius.com/sso/shopify/api/token?store=${lrshopifystore}&return_url=${path}`;

    try {
      response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: "Bearer " + lrtoken,
          "X-LoginRadius-ApiKey": raasoption.apiKey,
          "Content-Type": "application/json",
        },
      });

      if (200 === response.status) {
        data = await response.json();
        console.log("[ async exchangeMultipassToken() ] :: Success", data);
        return data;
      } else {
        data = false;
        return data;
      }
    } catch (error) {
      console.log("[ async exchangeMultipassToken() ] :: Error", error);
    } finally {
      console.log("[ async exchangeMultipassToken() ] :: Complete");
    }
  };

  const checkIfCareGiverClient = async (accHash = "") => {
    let data, response;

    try {
      response = await fetch(`/apps/api/client/is-shop-as/${accHash}`, {
        method: "GET",
        cache: "no-cache",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (200 === response.status) {
        data = await response.json();
        console.log("[ async checkIfCareGiverClient() ] :: Success", data);
        return data;
      } else {
        data = false;
        return data;
      }
    } catch (error) {
      console.log("[ async checkIfCareGiverClient() ] :: Error", error);
    } finally {
      console.log("[ async checkIfCareGiverClient() ] :: Complete");
    }
  };

  const ssoLogin = async (accessToken, exchangeMultipassToken, raas = raasoption) => {
    if (raas?.auth && raas?.apiKey && exchangeMultipassToken && accessToken) {
      try {
        let response = await fetch(`//${raas?.auth}/ssologin/setToken?token=${accessToken}&apiKey=${raas.apiKey}`, {
          method: "GET",
          cache: "no-cache",
          headers: {
            "Content-Type": "application/json",
          },
        });
        response = await response.json();

        if (response.istokenvalid) {
          await exchangeMultipassToken(accessToken);
          localStorage.setItem("LRTokenKey", accessToken);
          console.log("[ async ssoLogin() ] :: Success", data);
        }
      } catch (error) {
        console.log("[ async ssoLogin() ] :: Error", error);
      } finally {
        console.log("[ async ssoLogin() ] :: Complete");
      }
    }
  };

  return {
    addProductsToCart,
    changeCartByLineItemKey,
    checkIfCareGiverClient,
    clearCart,
    clearCartAttributes,
    fetchSectionHtml,
    getCart,
    getCustomerByID,
    getProductByHandle,
    getThemeAssetFromPath,
    updateCartAttribute,
    test,
    withClaimlineTrackingId,
    withClaimlineTrackingIds,
    exchangeMultipassToken,
    ssoLogin,
  };
})();
